// flowTemplates.js
// Constantes y helpers compartidos para el "Flow" de ventas (LeadEditModal +
// FlowPreviewModal). El "Flow" no es un Flow de la API de Meta: son dos
// plantillas de WhatsApp que envía conextcrm-e2f0b3:
//   1. Una plantilla "head" (texto con el nombre del lead). barbería/peluquerías/
//      estética comparten `conext_head7` (con {{2}}=tipo de negocio); lavadero usa
//      la suya (`lavadero_head7`, solo {{1}}).
//   2. `barber_mini2` (genérica) con una miniatura generada dinámicamente.

// Mapeo sector → plantilla "head" del Flow.
// barbería/peluquerías/estética comparten una única plantilla (`conext_head7`)
// con {{1}}=nombre y {{2}}=tipo de negocio. lavadero conserva su propia plantilla
// (`lavadero_head7`, solo {{1}}; misma estructura de bullets que conext_head7).
// Las antiguas barber_head5/peluquerias_head5/estetica_head5/conext_head6 y
// lavadero_head5/lavadero_head6 quedan deprecated (sin borrar) y sin usar.
export const FLOW_HEAD_TEMPLATE_BY_SECTOR = {
  barberia: "conext_head7",
  peluquerias: "conext_head7",
  lavadero: "lavadero_head7",
  estetica: "conext_head7",
  // Comida para llevar tiene su propia head (solo {{1}}): el copy de "citas" no
  // aplica; aquí se habla de menú por WhatsApp, pedidos y recogida.
  comida_para_llevar: "comida_head7",
};

// Variante ONLINE de la "head" por sector, para TELEVENTA. Idénticas a las de
// arriba salvo el cierre: en televenta no hay visita presencial, un asesor LLAMA
// por teléfono ("Todo online, sin visitas"). barbería/peluquerías/estética
// comparten `conext_head7_online` ({{1}}+{{2}}); lavadero usa `lavadero_head7_online`
// ({{1}}). Comida ya es online de serie (`comida_head7`), así que reusa la misma.
export const FLOW_HEAD_ONLINE_BY_SECTOR = {
  barberia: "conext_head7_online",
  peluquerias: "conext_head7_online",
  lavadero: "lavadero_head7_online",
  estetica: "conext_head7_online",
  comida_para_llevar: "comida_head7",
};

// ── Flow televenta: UNA sola plantilla ───────────────────────────────────────
// El "Flow televenta" se envía en frío, antes de que el cold caller llame, para que el
// lead tenga algo que VER antes de descolgar. A diferencia del resto de envíos (head de
// texto + mini con miniatura = 2 mensajes), va en UN SOLO mensaje:
//   HEADER VIDEO (el ad) + el texto de la head y el de la mini + los dos botones
//   ("Solicitar presentación" → demo online por videollamada, y "Simulador de cita").
// Lo que se pierde frente a los 2 mensajes: la miniatura personalizada con el nombre del
// negocio (una plantilla solo admite UN header, y aquí es el vídeo).
//
// Cada sector tiene SU plantilla (con su ad y su copy: el tipo de negocio va escrito en
// el cuerpo, así que solo llevan {{1}} — no el {{2}} de las heads compartidas). Comida
// para llevar tiene su copy orientada a PEDIDOS y su botón "Simulador de pedido"
// (payload `mkt_sim_pedido:comida_para_llevar`), no de citas.
// Añadir un sector = una línea en cada mapa + su plantilla (add_televenta_video_templates.js).
// (`pelu_head7_video_online`, la head de vídeo suelta de la variante de 2 mensajes, queda
// deprecada y sin usar — sin borrar, como el resto de heads antiguas.)
// ── Interruptor de la demo de 30 minutos ────────────────────────────────────────────
// Las plantillas de captación con header IMAGE decían "son 15 minutos por videollamada".
// Una plantilla APPROVED no se puede editar, así que la versión de 30 minutos son OCHO
// plantillas nuevas con sufijo `_v2` (mismo cuerpo, solo cambia la duración).
//
// Pasar a `true` CUANDO LAS 8 ESTÉN APPROVED en Meta (comprobarlo, no suponerlo: una
// PENDING no se puede enviar y el script aborta la tanda entera). Los scripts de envío
// transpilan este fichero, así que el cambio surte efecto sin desplegar; desplegar
// `ventas` solo hace falta para que el botón del CRM use también las nuevas.
export const TELEVENTA_DEMO_30MIN = true; // ON 19/8/2026: las 8 _v2 APPROVED, verificado

/** Añade el sufijo _v2 a las plantillas de captación cuando la demo es de 30 minutos. */
function conDuracion(nombre) {
  return TELEVENTA_DEMO_30MIN ? `${nombre}_v2` : nombre;
}

export const FLOW_TELEVENTA_PELU = "pelu_televenta_video";

export const FLOW_TELEVENTA_BY_SECTOR = {
  peluquerias: FLOW_TELEVENTA_PELU,
  barberia: "barber_televenta_video",
  estetica: "estetica_televenta_video",
  lavadero: "lavadero_televenta_video",
  comida_para_llevar: "comida_televenta_video",
  taller: conDuracion("taller_televenta_img"),
  canina: conDuracion("canina_televenta_img"),
  tatuajes: conDuracion("tattoo_televenta_img"),
  fisioterapia: conDuracion("fisio_televenta_img"),
  // Autoescuela (9/9/2026) NACE con botón de baja: nunca hubo `autoescuela_televenta_img`
  // ni su `_v2` en Meta — las únicas plantillas creadas son las `_ob`. Por eso aquí NO se
  // llama a conDuracion() (no existe un `_v2` que sufijar) y el nombre es el mismo que en
  // FLOW_TELEVENTA_OB_BY_SECTOR: si algún día se apagara TELEVENTA_OPTOUT, el fallback
  // seguiría nombrando una plantilla REAL en vez de una que no existe.
  autoescuela: "autoescuela_f1_gestion_ob",
};

// ── Test A/B de copy en captación fría (2026-08) ────────────────────────────
// Dos variantes de CUERPO para los sectores "solo televenta" con imagen (taller,
// canina, tatuajes, fisioterapia): "gestion" (las de siempre, FLOW_TELEVENTA_BY_SECTOR,
// enfocadas a resolver el día a día del negocio) y "crecimiento" (*_mkt_img, enfocadas
// a captar más clientes). Mismo póster (FLOW_TELEVENTA_HEADER_BY_SECTOR, más abajo) y
// mismo botón FLOW "Solicitar presentación" en las dos — solo cambia el cuerpo.
// Sectores sin variante "crecimiento" producida/aprobada no tienen entrada aquí.
export const FLOW_TELEVENTA_MKT_BY_SECTOR = {
  taller: conDuracion("taller_mkt_img"),
  canina: conDuracion("canina_mkt_img"),
  tatuajes: conDuracion("tattoo_mkt_img"),
  fisioterapia: conDuracion("fisio_mkt_img"),
};

// ── Plantillas CON BOTÓN DE BAJA (2026-08-30) ───────────────────────────────
// Meta pide que toda plantilla MARKETING ofrezca una salida, y que la salida sea fácil de
// encontrar: si no lo es, la que el destinatario encuentra es *Bloquear* o *Reportar*, y eso
// lo paga la calidad del número — compartido con clientes reales en modo Conext.
//
// El CUERPO es idéntico al de las `_v2` que ya se estaban enviando; lo único que cambia es
// que llevan un segundo botón QUICK_REPLY "No quiero recibir más". Se mantiene idéntico a
// propósito: si el rendimiento cambia, el cambio es atribuible al botón y no a otro copy.
// Una plantilla APPROVED no se puede editar, de ahí que sean plantillas nuevas.
//
// Pasar a `true` CUANDO LAS 12 ESTÉN APPROVED (comprobarlo, no suponerlo: el sender aborta
// la tanda entera si alguna sigue PENDING). Los scripts transpilan este fichero, así que el
// cambio surte efecto sin desplegar nada.
export const TELEVENTA_OPTOUT = true; // ON 30/8/2026: las 12 APPROVED, verificado por API

const FLOW_TELEVENTA_OB_BY_SECTOR = {
  taller: "taller_f1_gestion_ob",
  canina: "canina_f1_gestion_ob",
  tatuajes: "tattoo_f1_gestion_ob",
  fisioterapia: "fisio_f1_gestion_ob",
  autoescuela: "autoescuela_f1_gestion_ob",
};

const FLOW_TELEVENTA_MKT_OB_BY_SECTOR = {
  taller: "taller_f1_crece_ob",
  canina: "canina_f1_crece_ob",
  tatuajes: "tattoo_f1_crece_ob",
  fisioterapia: "fisio_f1_crece_ob",
};

// Plantilla de la fase 2 (seguimiento). Sin header y con el mismo botón de baja.
export const FLOW_SEGUIMIENTO_BY_SECTOR = {
  taller: "taller_f2_ob",
  canina: "canina_f2_ob",
  tatuajes: "tattoo_f2_ob",
  fisioterapia: "fisio_f2_ob",
  autoescuela: "autoescuela_f2_ob",
};

// Texto EXACTO del botón de baja. Lo consume `marketingOptOut.js` como red de seguridad
// cuando un envío no manda el payload `mkt_optout:<sector>`. Cambiarlo aquí sin cambiarlo
// allí deja bajas sin detectar — y una baja sin detectar es una denuncia en potencia.
export const TELEVENTA_OPTOUT_BUTTON_TEXT = "No quiero recibir más";

// Variantes válidas del test A/B (para validar --variant en los scripts de envío).
export const TELEVENTA_AB_VARIANTS = ["gestion", "crecimiento"];

// ── Comida para llevar: A/B de TRES brazos (7/9/2026) ────────────────────────
// Mide por WhatsApp lo mismo que la campaña de Meta Ads mide por paid: qué ángulo consigue
// más demos. Cuerpo y vídeo van juntos —son el ángulo—; cierre y botón son idénticos.
//   dia        ← ad kling-f   cerrar el día con los pedidos hechos y pagados
//   mecanismo  ← ad kling-d   así pide un cliente: platos, hora, pago por adelantado
//   horapunta  ← ad kling-e   en hora punta, cada llamada que no coges es un pedido que se va
export const COMIDA_AB_VARIANTS = ["dia", "mecanismo", "horapunta"];
export const COMIDA_TEMPLATE_BY_VARIANT = {
  dia: "comida_f1_dia_ob",
  mecanismo: "comida_f1_mecanismo_ob",
  horapunta: "comida_f1_horapunta_ob",
};
// Cada variante lleva SU vídeo (el mismo que el ad), comprimido a 720p (~7 MB; los de 1080p
// pesaban 17-18 MB y Meta corta la cabecera de vídeo en 16).
export const COMIDA_HEADER_BY_VARIANT = {
  dia: { type: "VIDEO", url: `${"https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/shared-assets/flow"}/televenta_comida_f.mp4` },
  mecanismo: { type: "VIDEO", url: `${"https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/shared-assets/flow"}/televenta_comida_d.mp4` },
  horapunta: { type: "VIDEO", url: `${"https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/shared-assets/flow"}/televenta_comida_e.mp4` },
};

// Nombre de la plantilla de televenta del sector para una VARIANTE de copy del test A/B.
// Sin `variant` (o con "gestion") devuelve EXACTAMENTE lo mismo que
// televentaTemplateNameForSector(sector) — cero regresión para los callers existentes,
// que deben seguir devolviendo la variante "gestion" por defecto. "crecimiento" cae a
// "gestion" si el sector aún no tiene esa variante (sin producir/aprobar en Meta).
export function televentaTemplateNameForSectorVariant(sector, variant = "gestion") {
  if (sector === "comida_para_llevar" && COMIDA_TEMPLATE_BY_VARIANT[variant]) {
    return COMIDA_TEMPLATE_BY_VARIANT[variant];
  }
  // Con el botón de baja activo mandan los mapas `_ob`; si el sector no tiene versión con
  // botón se cae a la de siempre, para no dejar un sector sin poder enviar.
  if (TELEVENTA_OPTOUT) {
    if (variant === "crecimiento" && FLOW_TELEVENTA_MKT_OB_BY_SECTOR[sector]) {
      return FLOW_TELEVENTA_MKT_OB_BY_SECTOR[sector];
    }
    if (FLOW_TELEVENTA_OB_BY_SECTOR[sector]) return FLOW_TELEVENTA_OB_BY_SECTOR[sector];
  }
  if (variant === "crecimiento" && FLOW_TELEVENTA_MKT_BY_SECTOR[sector]) {
    return FLOW_TELEVENTA_MKT_BY_SECTOR[sector];
  }
  return televentaTemplateNameForSector(sector);
}

// mp4 público (bucket conextcrm-uploads) que se pasa en `templateExtras.header_url` al
// enviar. Es el MISMO fichero que el sample que aprobó Meta: reemplazar el objeto de S3
// cambia el ad sin volver a pasar por aprobación.
const FLOW_VIDEO_BASE =
  "https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/shared-assets/flow";

// Base de los pósters de "resumen" (mismo bucket, otra carpeta). Los sectores sin ad en
// vídeo (taller) usan aquí su header IMAGE.
const RESUMEN_BASE =
  "https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/shared-assets/resumen";

// Cabecera del mensaje único de televenta, por sector. Los sectores con ad producido van
// con VIDEO; los que no tienen vídeo (taller) van con IMAGE = su póster del resumen, que
// es además el sample aprobado por Meta.
export const FLOW_TELEVENTA_HEADER_BY_SECTOR = {
  peluquerias: { type: "VIDEO", url: `${FLOW_VIDEO_BASE}/televenta_peluquerias.mp4` },
  barberia: { type: "VIDEO", url: `${FLOW_VIDEO_BASE}/televenta_barberia.mp4` },
  estetica: { type: "VIDEO", url: `${FLOW_VIDEO_BASE}/televenta_estetica.mp4` },
  lavadero: { type: "VIDEO", url: `${FLOW_VIDEO_BASE}/televenta_lavadero.mp4` },
  comida_para_llevar: { type: "VIDEO", url: `${FLOW_VIDEO_BASE}/televenta_comida_para_llevar.mp4` },
  taller: { type: "IMAGE", url: `${RESUMEN_BASE}/resumen_taller.png` },
  canina: { type: "IMAGE", url: `${RESUMEN_BASE}/resumen_canina.png` },
  tatuajes: { type: "IMAGE", url: `${RESUMEN_BASE}/resumen_tatuajes.png` },
  fisioterapia: { type: "IMAGE", url: `${RESUMEN_BASE}/resumen_fisioterapia.png` },
  autoescuela: { type: "IMAGE", url: `${RESUMEN_BASE}/resumen_autoescuela.png` },
};

// Cabecera (tipo + url) de la plantilla única de televenta del sector, o undefined si no
// tiene flow de televenta.
export function televentaHeaderForSector(sector, variant) {
  if (sector === "comida_para_llevar" && variant && COMIDA_HEADER_BY_VARIANT[variant]) {
    return COMIDA_HEADER_BY_VARIANT[variant];
  }
  return FLOW_TELEVENTA_HEADER_BY_SECTOR[sector];
}

// Fachada histórica: solo los sectores con cabecera de VÍDEO. Un sector con header IMAGE
// (taller) devuelve undefined a propósito.
export const FLOW_TELEVENTA_VIDEO_URL_BY_SECTOR = Object.fromEntries(
  Object.entries(FLOW_TELEVENTA_HEADER_BY_SECTOR)
    .filter(([, h]) => h.type === "VIDEO")
    .map(([s, h]) => [s, h.url]),
);

// ¿El sector tiene flow de televenta (plantilla única + cabecera)? Gatea el botón.
export function hasTeleventaFlow(sector) {
  return !!(FLOW_TELEVENTA_BY_SECTOR[sector] && FLOW_TELEVENTA_HEADER_BY_SECTOR[sector]);
}

// Nombre de la plantilla única de televenta del sector (o undefined si no tiene).
export function televentaTemplateNameForSector(sector) {
  return FLOW_TELEVENTA_BY_SECTOR[sector];
}

// Email de televenta (Resend, v_email_templates): mismo copy/vídeo que el Flow de
// WhatsApp, un template por sector, seedeado por ventas/scrap/seed_televenta_email_templates.js.
// Un sector sin flow televenta (sin ad) tampoco tiene email de televenta.
export function televentaEmailTemplateIdForSector(sector) {
  return hasTeleventaFlow(sector) ? `televenta_email_${sector}` : null;
}

// URL del mp4 de la cabecera de la plantilla de televenta (o undefined).
export function televentaVideoUrlForSector(sector) {
  return FLOW_TELEVENTA_VIDEO_URL_BY_SECTOR[sector];
}

// ── "Marketing por WhatsApp" (servicio de captación/reenganche, NO el CRM) ──────────
// Mismo patrón que FLOW_TELEVENTA_*: una plantilla única por sector (vídeo de ejemplo +
// presentación del servicio + botón FLOW "Solicitar presentación", MISMO flow_id que el
// del CRM). Ver docs/superpowers/specs/2026-07-22-marketing-televenta-flow-design.md.
// v2 (2026-07-22): el body de v1 encuadraba el servicio solo como "os generamos vídeos";
// v2 dice que ayudamos a DISEÑAR la campaña entera (fiestas locales/nacionales,
// promociones). Un body no se puede editar en Meta (APPROVED/PENDING lo bloquean) → nombre
// nuevo por sector (admin_scripts/add_marketing_televenta_templates.js), v1 queda huérfana.
export const FLOW_MARKETING_BY_SECTOR = {
  peluquerias: "pelu_marketing_video_v2",
  barberia: "barber_marketing_video_v2",
  estetica: "estetica_marketing_video_v2",
  lavadero: "lavadero_marketing_video_v2",
  comida_para_llevar: "comida_marketing_video_v2",
};

export const FLOW_MARKETING_VIDEO_URL_BY_SECTOR = {
  peluquerias: `${FLOW_VIDEO_BASE}/marketing_televenta_peluquerias.mp4`,
  barberia: `${FLOW_VIDEO_BASE}/marketing_televenta_barberia.mp4`,
  estetica: `${FLOW_VIDEO_BASE}/marketing_televenta_estetica.mp4`,
  lavadero: `${FLOW_VIDEO_BASE}/marketing_televenta_lavadero.mp4`,
  comida_para_llevar: `${FLOW_VIDEO_BASE}/marketing_televenta_comida_para_llevar.mp4`,
};

export function hasMarketingFlow(sector) {
  return !!(FLOW_MARKETING_BY_SECTOR[sector] && FLOW_MARKETING_VIDEO_URL_BY_SECTOR[sector]);
}

export function marketingTemplateNameForSector(sector) {
  return FLOW_MARKETING_BY_SECTOR[sector];
}

export function marketingVideoUrlForSector(sector) {
  return FLOW_MARKETING_VIDEO_URL_BY_SECTOR[sector];
}

export function marketingEmailTemplateIdForSector(sector) {
  return hasMarketingFlow(sector) ? `marketing_email_${sector}` : null;
}

// ── Payload del botón "Simulador de cita"/"Simulador de pedido" ───────────────
// El texto del botón es el mismo para todos los sectores, así que sin payload Meta manda
// el TEXTO y c_processOnboarding no sabe de qué sector es el lead → abría SIEMPRE el
// simulador de barbería. El payload se manda en el ENVÍO (no vive en la plantilla), así
// que arreglarlo no requiere recrear ni reaprobar nada.
//
// c_processOnboarding hace `[action, sector] = payload.split(":")`:
//   - `mkt_sim_cita:<sector>`   → simulador de CITAS de ese sector.
//   - `mkt_sim_pedido:<sector>` → simulador de PEDIDOS (se intercepta antes; comida).
// Los sectores tienen que ser los mismos identificadores que usa el simulador aguas
// abajo (c_detectPeluqueriaAppointmentIntent): barberia · peluquerias · estetica · lavadero.
export const FLOW_SIMULATOR_PAYLOAD_BY_SECTOR = {
  barberia: "mkt_sim_cita:barberia",
  peluquerias: "mkt_sim_cita:peluquerias",
  estetica: "mkt_sim_cita:estetica",
  lavadero: "mkt_sim_cita:lavadero",
  comida_para_llevar: "mkt_sim_pedido:comida_para_llevar",
};

// Payload del botón de simulador para el sector, o undefined si el sector no tiene
// simulador propio (spa, salud, autoescuela…): en ese caso NO se manda payload y se deja
// el fallback por texto de siempre, en vez de pedir un simulador que no existe.
export function simulatorPayloadForSector(sector) {
  return FLOW_SIMULATOR_PAYLOAD_BY_SECTOR[sector];
}

// Plantilla "head" única compartida. Distinta de las per-sector: lleva un
// segundo parámetro posicional ({{2}}) con el tipo de negocio.
export const FLOW_HEAD_SHARED = "conext_head7";
// Variante online de la head compartida (también lleva {{2}}).
export const FLOW_HEAD_SHARED_ONLINE = "conext_head7_online";

// ¿La head lleva un 2º parámetro posicional ({{2}} = tipo de negocio)?
// Solo las compartidas conext_head7 / conext_head7_online; el resto (lavadero,
// comida, fallback) solo usan {{1}}.
export function headUsesBusinessType(headName) {
  return headName === FLOW_HEAD_SHARED || headName === FLOW_HEAD_SHARED_ONLINE;
}

// Tipo de negocio para el {{2}} de la head compartida, por sector.
export const FLOW_BUSINESS_TYPE_BY_SECTOR = {
  barberia: "barbería",
  peluquerias: "peluquería",
  estetica: "clínica estética",
  lavadero: "lavadero",
};

// Devuelve el tipo de negocio (para {{2}}) del sector, o "" si no aplica.
export function businessTypeForSector(sector) {
  return FLOW_BUSINESS_TYPE_BY_SECTOR[sector] || "";
}

// Plantilla "mini" genérica (la imagen del header se sustituye por la miniatura
// generada con /ventas/generate-thumbnail). Solo lleva el botón "Simulador de cita".
export const FLOW_MINI_TEMPLATE = "barber_mini2";

// Variante de la "mini" con DOS botones: "Solicitar presentación" (botón FLOW que abre
// el flow de reserva de demo comercial con el vendedor asignado) + "Simulador de cita".
// Se usa solo en el envío "Flow + presentación" (no reemplaza a la mini normal).
export const FLOW_MINI_PRESENTATION = "presentacion_mini";

// Variante ONLINE de la mini con presentación: mismo cuerpo y botones, pero el
// botón FLOW abre el flow de demo ONLINE (videollamada, sin pantalla de elegir
// modalidad). Se usa en televenta. Ver presentation_online_flow_id.
export const FLOW_MINI_PRESENTATION_ONLINE = "presentacion_online_mini";

// Plantillas "mini" específicas por sector. La mayoría de sectores comparten las
// genéricas de arriba (botón "Simulador de cita"); comida para llevar tiene las
// suyas con el botón "Simulador de pedido". El botón FLOW "Solicitar presentación"
// reusa el mismo flow de demo comercial (sector-agnóstico).
export const FLOW_MINI_BY_SECTOR = {
  comida_para_llevar: { normal: "comida_mini2", presentation: "comida_presentacion_mini" },
};

// Nombre de la plantilla "mini" para un sector y modo (normal vs con presentación).
// Cae a las genéricas (barber_mini2 / presentacion_mini) si el sector no tiene propias.
// `online` (televenta) → la mini con presentación usa la variante de flow online
// (videollamada). La mini "normal" (solo simulador) no se ve afectada por online.
export function miniTemplateNameForSector(sector, withPresentation = false, online = false) {
  const perSector = FLOW_MINI_BY_SECTOR[sector];
  // comida ya usa su flow online de serie en `presentation`; no hay variante extra.
  if (perSector) return withPresentation ? perSector.presentation : perSector.normal;
  if (!withPresentation) return FLOW_MINI_TEMPLATE;
  return online ? FLOW_MINI_PRESENTATION_ONLINE : FLOW_MINI_PRESENTATION;
}

// Fallback de "head" cuando el sector no tiene plantilla específica.
export const FLOW_HEAD_FALLBACK = "barber_head4";

// Sectores con plantilla "head" específica (los que ofrece el selector).
export const FLOW_SUPPORTED_SECTORS = [
  "barberia",
  "peluquerias",
  "lavadero",
  "estetica",
  "comida_para_llevar",
];

// Sectores que SOLO tienen Flow televenta (una plantilla única con header
// IMAGE/VIDEO + botón(es), sin head+mini ni "Flow normal"/"Flow + presentación").
// A PROPÓSITO no se meten en FLOW_SUPPORTED_SECTORS: esa lista habilita las
// variantes de 2 mensajes (head+mini), que para estos sectores caerían al
// fallback FLOW_HEAD_FALLBACK/FLOW_MINI_TEMPLATE (barber_head4/barber_mini2,
// copy de barbería) por no tener sus propias plantillas head/mini.
// taller: solo `taller_televenta_img` (header IMAGE = póster, UN SOLO botón
// FLOW "Solicitar presentación", sin botón de simulador).
export const FLOW_TELEVENTA_ONLY_SECTORS = ["taller", "canina", "tatuajes", "fisioterapia", "autoescuela"];

// Todos los sectores elegibles/previsualizables desde el Flow de ventas: los de
// siempre (head+mini, `FLOW_SUPPORTED_SECTORS`) + los que solo tienen Flow
// televenta (`FLOW_TELEVENTA_ONLY_SECTORS`). Úsalo en selectores de sector
// (chips del preview, "este lead no tiene sector…"); para decidir si un sector
// soporta "Flow normal"/"Flow + presentación" sigue siendo FLOW_SUPPORTED_SECTORS.
export const FLOW_PREVIEWABLE_SECTORS = [
  ...FLOW_SUPPORTED_SECTORS,
  ...FLOW_TELEVENTA_ONLY_SECTORS,
];

export const SECTOR_LABELS = {
  barberia: "Barbería",
  peluquerias: "Peluquería",
  estetica: "Estética",
  spa: "Spa",
  salud: "Salud",
  lavadero: "Lavadero",
  taller: "Taller mecánico",
  canina: "Peluquería canina",
  tatuajes: "Tatuajes",
  fisioterapia: "Fisioterapia",
  osteopatia: "Osteopatía",
  psicologia: "Psicología",
  bares_deportes: "Bar Deportes",
  bares_tematicos: "Bar Temático",
  consultoria: "Consultoría",
  despacho_abogados: "Abogados",
  autoescuela: "Autoescuela",
  pizzeria: "Pizzería",
  comida_para_llevar: "Comida para Llevar",
  restaurante: "Restaurante",
  distribucion: "Distribución",
  otro: "Otro",
};

// Nombre de la plantilla "head" para un sector (con fallback).
// `online` (televenta) → variante con cierre online ("te llamaremos / sin visita").
// Si no existe variante online del sector, cae a la presencial.
// Nota: el "Flow televenta" NO pasa por aquí — va por su plantilla única
// (televentaTemplateNameForSector), que no es una head.
export function headTemplateNameForSector(sector, online = false) {
  if (online) {
    return (
      FLOW_HEAD_ONLINE_BY_SECTOR[sector] ||
      FLOW_HEAD_TEMPLATE_BY_SECTOR[sector] ||
      FLOW_HEAD_FALLBACK
    );
  }
  return FLOW_HEAD_TEMPLATE_BY_SECTOR[sector] || FLOW_HEAD_FALLBACK;
}

/**
 * Inyecta una URL en la cabecera multimedia de una plantilla, sin mutar el original.
 * Devuelve una copia de los components con el `header_handle` del HEADER sustituido
 * por `mediaUrl`. Sirve tanto para la miniatura del mini (IMAGE) como para el mp4 de
 * la head de televenta (VIDEO): ambos se previsualizan desde `header_handle[0]`.
 */
function withInjectedHeaderMedia(tpl, mediaUrl) {
  if (!tpl || !mediaUrl) return tpl || null;
  const components = (tpl.components || []).map((c) => {
    if (c.type !== "HEADER") return c;
    return {
      ...c,
      example: { ...(c.example || {}), header_handle: [mediaUrl] },
    };
  });
  return { ...tpl, components };
}

/**
 * Construye las plantillas a previsualizar para un sector.
 * @param {Array} allTemplates - plantillas devueltas por GET /plantillas.
 * @param {string} sector - sector seleccionado.
 * @param {string} [thumbUrl] - miniatura generada para inyectar en el mini.
 * @param {string} [miniName] - nombre de la plantilla "mini". Si no se pasa, se
 *   resuelve per-sector (mini "normal" del sector). Pasa el nombre del mini con
 *   presentación para previsualizar la variante con 2 botones.
 * @param {boolean} [online] - televenta: previsualiza la head con cierre online.
 * @param {boolean} [televenta] - Flow televenta: un ÚNICO mensaje (la plantilla con el
 *   vídeo y los dos botones), en vez de head + mini. Solo en sectores con ad.
 * @returns {{ head: object|null, mini: object|null, single: object|null }}
 *   `single` != null → es un único mensaje; head y mini van a null (y viceversa).
 */
export function buildFlowPreviewTemplates(
  allTemplates,
  sector,
  thumbUrl,
  miniName,
  online = false,
  televenta = false,
) {
  const list = Array.isArray(allTemplates) ? allTemplates : [];

  // Flow televenta: un solo mensaje. Se inyecta el media de la cabecera (mp4 o póster)
  // para poder previsualizarlo.
  if (televenta && hasTeleventaFlow(sector)) {
    const raw = list.find((t) => t.name === televentaTemplateNameForSector(sector)) || null;
    const single = withInjectedHeaderMedia(raw, televentaHeaderForSector(sector)?.url);
    return { head: null, mini: null, single };
  }

  const resolvedMiniName = miniName || miniTemplateNameForSector(sector, false, online);
  const headName = headTemplateNameForSector(sector, online);
  const head = list.find((t) => t.name === headName) || null;
  const rawMini = list.find((t) => t.name === resolvedMiniName) || null;
  const mini = thumbUrl ? withInjectedHeaderMedia(rawMini, thumbUrl) : rawMini;
  return { head, mini, single: null };
}
